import { isNil, each, isArray, size, map, first, filter, forEach, extend, includes, intersection, keys, get, concat } from 'lodash';
import * as moment_ from 'moment';
import { v4 } from 'uuid';
import { from, forkJoin, throwError, Subject, of, isObservable, BehaviorSubject, concat as concat$1, interval } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ɵɵinject, ɵɵdefineInjectable, ɵsetClassMetadata, Injectable, ɵɵdefineComponent, ɵɵelementStart, ɵɵtext, ɵɵelementEnd, ɵɵadvance, ɵɵproperty, Component, ApplicationRef, ɵɵdefineNgModule, ɵɵdefineInjector, ɵɵsetNgModuleScope, NgModule } from '@angular/core';
import { catchError, concatMap, tap, map as map$1, first as first$1, filter as filter$1 } from 'rxjs/operators';
import { SwUpdate } from '@angular/service-worker';
import { MatDialogTitle, MatDialogContent, MatDialogActions, MatDialogClose, MatDialog, MatDialogModule } from '@angular/material/dialog';
import { DefaultLayoutDirective, DefaultLayoutAlignDirective } from '@angular/flex-layout/flex';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FlexLayoutModule } from '@angular/flex-layout';

const moment = moment_;
var DatabaseType;
(function (DatabaseType) {
    DatabaseType[DatabaseType["Resource"] = 0] = "Resource";
    DatabaseType[DatabaseType["User"] = 1] = "User";
    DatabaseType[DatabaseType["Document"] = 2] = "Document";
})(DatabaseType || (DatabaseType = {}));
class DatabaseRecord {
    constructor(data = {}) {
        const type = this.constructor;
        this.key = data.key || data.id || data.uuid || null;
        if (isNil(this.key) && type.autoCreateUUIDs) {
            this.key = v4();
            this.createdAt = moment();
        }
        if (type.fields && type.fields.length > 0) {
            each(type.fields, field => {
                this[field] = data[field];
                const fieldName = field.toLowerCase();
                if (fieldName.includes('date') || fieldName.includes('day') || field.endsWith('At')) {
                    if (fieldName !== 'dayuuid') {
                        this[field] = moment(data[field]);
                    }
                }
            });
        }
        if (data.createdAt) {
            this.createdAt = moment(data.createdAt);
        }
        if (data.updatedAt) {
            this.updatedAt = moment(data.updatedAt);
        }
        if (data.deletedAt) {
            this.deletedAt = moment(data.deletedAt);
        }
    }
    get lastUpdate() {
        return moment.max(this.createdAt || moment(0), this.updatedAt || moment(0), this.deletedAt || moment(0));
    }
    get uuid() {
        return this.key;
    }
    set uuid(key) {
        this.key = key;
    }
    static parseDate(date) {
        return moment(date);
    }
    get isDirty() {
        return this.isNew || this.isUpdated || this.isDeleted;
    }
    get isNew() {
        return !isNil(this.createdAt);
    }
    get isUpdated() {
        return !isNil(this.updatedAt);
    }
    get isDeleted() {
        return !isNil(this.deletedAt);
    }
    markClean() {
        this.createdAt = null;
        this.updatedAt = null;
        this.deletedAt = null;
    }
    markDirty() {
        if (this.uuid && !this.createdAt) {
            this.markUpdated();
        }
        else if (!this.createdAt) {
            this.markCreated();
        }
    }
    generateId() {
        this.uuid = v4();
    }
    markCreated() {
        this.generateId();
        this.createdAt = moment();
    }
    markUpdated() {
        this.updatedAt = moment();
    }
    markDeleted() {
        this.deletedAt = moment();
    }
    toJSON(fields) {
        const object = {
            key: this.key,
        };
        if (isNil(fields)) {
            fields = [];
        }
        const type = this.constructor;
        if (type.fields && type.fields.length > 0) {
            fields = fields.concat(type.fields);
        }
        each(fields, field => {
            let value = this[field];
            // look at the private cache of foreign records if we never loaded records
            // if (isNil(value) && !isNil(this['_' + field])) {
            //   value =
            // }
            if (!isNil(value)) {
                if (moment.isMoment(value)) {
                    value = value.toISOString();
                }
                else if (isArray(value) && size(value) > 0 && value[0] instanceof DatabaseRecord) {
                    value = map(value, record => record.key);
                }
                else if (value instanceof DatabaseRecord) {
                    value = value.toJSON();
                }
                if (!isNil(value)) {
                    object[field] = value;
                }
            }
        });
        if (this.createdAt) {
            object.createdAt = this.createdAt.toISOString();
        }
        if (this.updatedAt) {
            object.updatedAt = this.updatedAt.toISOString();
        }
        if (this.deletedAt) {
            object.deletedAt = this.deletedAt.toISOString();
        }
        return object;
    }
}
DatabaseRecord.recordName = '';
DatabaseRecord.objectStoreName = '';
DatabaseRecord.fields = [];
DatabaseRecord.autoCreateUUIDs = true;
DatabaseRecord.databaseType = DatabaseType.Document;

class DocumentRecord extends DatabaseRecord {
    constructor(data) {
        super(data);
        this.version = data.version;
        this.document = data.document;
    }
}
DocumentRecord.fields = ['version', 'userUUID', 'document'];
DocumentRecord.databaseType = DatabaseType.Document;

const moment$1 = moment_;
class DatabaseService {
    constructor(syncLog) {
        this.syncLog = syncLog;
        this.connecting = false;
    }
    connect() {
        if (this.connecting) {
            return new Promise(resolve => {
                setTimeout(resolve, 1000);
            });
        }
        return new Promise((resolve, reject) => {
            if (this.db) {
                // if we're already connected, just resolve
                return resolve();
            }
            this.connecting = true;
            const request = window.indexedDB.open(this.options.dbName, this.options.version);
            request.onsuccess = event => {
                this.syncLog.trace(`Connected to ${this.options.dbName} version ${this.options.version}.`);
                this.db = event.target.result;
                this.db.onerror = this.onDatabaseError.bind(this);
                this.connecting = false;
                this.seedDatabase().then(() => {
                    if (this.options.connected) {
                        this.options.connected(this).subscribe(() => resolve());
                    }
                    else {
                        resolve();
                    }
                });
            };
            request.onerror = event => {
                this.syncLog.trace(`Failed to connect to ${this.options.dbName} version ${this.options.version}.`);
                reject(event);
            };
            request.onupgradeneeded = event => {
                this.db = event.target.result;
                this.syncLog.trace(`DB Upgrade: Upgrading database ${this.options.dbName}.`);
                if (this.options.upgraded) {
                    this.options.upgraded(this).subscribe();
                }
            };
        });
    }
    getVersion() {
        return this.db.version;
    }
    get(model = DatabaseRecord, where) {
        if (typeof where === 'string') {
            return this.getByKey(model, where);
        }
        else {
            return this.queryOne(model, where);
        }
    }
    getByKey(model, key) {
        const objectStoreName = model.objectStoreName;
        return new Promise((resolve, reject) => {
            this.syncLog.trace(`${objectStoreName}: Getting ${key} from object store…`);
            const transaction = this.transaction(objectStoreName, resolve, reject);
            const objectStore = transaction.objectStore(objectStoreName);
            const request = objectStore.get(key);
            request.onerror = event => {
                this.syncLog.error(event, `${objectStoreName}: Error getting ${key} from object store.`);
                reject(event);
            };
            request.onsuccess = event => {
                this.syncLog.trace(`${objectStoreName}: Got ${key} from object store.`);
                if (!isNil(event.target.result)) {
                    const record = new model(event.target.result);
                    resolve(record);
                }
                else {
                    resolve(null);
                }
            };
        });
    }
    queryOne(model, where) {
        return this.query(model, where).then((records) => {
            const record = first(records);
            return record;
        });
    }
    query(model, where) {
        return this.getAll(model).then((records) => {
            const whereFilter = {};
            each(where, (value, key) => {
                if (value instanceof DatabaseRecord) {
                    whereFilter[key] = value.key;
                }
                else {
                    whereFilter[key] = value;
                }
            });
            records = filter(records, whereFilter);
            return records;
        });
    }
    getAll(model) {
        return new Promise((resolve, reject) => {
            const objectStoreName = model.objectStoreName;
            this.syncLog.trace(`${objectStoreName}: Getting all records from object store.`);
            const transaction = this.transaction(objectStoreName, resolve, reject);
            const objectStore = transaction.objectStore(objectStoreName);
            if (!objectStore) {
                const err = new Error('Object store does not exist: ' + objectStoreName);
                this.syncLog.error(err, `${objectStoreName}: Object store does not exist: ${objectStoreName}.`);
                reject(err);
                return;
            }
            let request;
            const timeStart = performance.now();
            try {
                request = objectStore.getAll();
            }
            catch (ex) {
                reject(ex);
                return;
            }
            request.onerror = event => {
                this.syncLog.error(event, `${objectStoreName}: Error getting all records from object store.`);
                reject(event);
            };
            request.onsuccess = event => {
                const records = map(event.target.result, record => {
                    return new model(record);
                });
                const timeEnd = performance.now();
                this.syncLog.trace(`${objectStoreName}: Got ${records.length} record(s) from object store in ${Math.round(timeEnd - timeStart)} ms.`);
                resolve(records);
            };
        });
    }
    /**
     * Insert records into the datastore.
     *
     * @param objectStoreName Object store name (table name).
     * @param records Database records to insert.
     * @param forceUpdate Set to true to force an update if the record already exists.
     */
    insert(model, recordOrRecords, forceUpdate, filters) {
        return new Promise((resolve, reject) => {
            const self = this;
            const records = (isArray(recordOrRecords) ? recordOrRecords : [recordOrRecords]);
            if (records.length === 0) {
                resolve();
                return;
            }
            // We do the reverse storage like our API
            if (filters && filters.options && filters.options.append) {
                forEach(records, (record, i) => {
                    extend(record, filters.options.append);
                });
            }
            const objectStoreName = model.objectStoreName;
            this.syncLog.trace(`${objectStoreName}: Inserting ${records.length} into object store...`);
            const timeStart = performance.now();
            const transaction = this.transaction(objectStoreName, resolve, reject);
            const objectStore = transaction.objectStore(objectStoreName);
            const promises = map(records, (record, index) => {
                return new Promise((resolveInsert, rejectInsert) => {
                    const obj = record.toJSON();
                    if (isNil(obj.key)) {
                        this.syncLog.warn(`${objectStoreName}: Attempting to insert record at index ${index} without a key.`);
                    }
                    const request = forceUpdate ? objectStore.put(obj) : objectStore.add(obj);
                    request.onerror = event => {
                        self.syncLog.error(event, `${objectStoreName}: Error inserting record at index ${index}.`);
                        rejectInsert(event);
                    };
                    request.onsuccess = event => {
                        const key = event.target.result;
                        const timeEnd = performance.now();
                        self.syncLog.trace(`${objectStoreName}: ${key} inserted in ${Math.round(timeEnd - timeStart)} ms.`);
                        resolveInsert();
                    };
                });
            });
            Promise.all(promises).then(() => resolve(), reject);
        });
    }
    update(record) {
        return new Promise((resolve, reject) => {
            const objectStoreName = record.constructor.objectStoreName;
            this.syncLog.trace(`${objectStoreName}: Updating ${record.key} in object store…`);
            const timeStart = performance.now();
            const transaction = this.transaction(objectStoreName, resolve, reject);
            const objectStore = transaction.objectStore(objectStoreName);
            const request = objectStore.put(record.toJSON());
            request.onerror = event => {
                this.syncLog.error(event, `${objectStoreName}: Error updating ${record.key} in object store.`);
                reject(event);
            };
            request.onsuccess = event => {
                const timeEnd = performance.now();
                this.syncLog.trace(`${objectStoreName}: ${record.key} updated in ${Math.round(timeEnd - timeStart)} ms.`);
                resolve();
            };
        });
    }
    delete(model, record) {
        const objectStoreName = record.constructor.objectStoreName;
        return this.deleteByKey(objectStoreName, record.key);
    }
    deleteByKey(objectStoreName, key) {
        return new Promise((resolve, reject) => {
            this.syncLog.trace(`${objectStoreName}: Deleting ${key} from object store…`);
            const timeStart = performance.now();
            const transaction = this.transaction(objectStoreName, resolve, reject);
            const objectStore = transaction.objectStore(objectStoreName);
            const request = objectStore.delete(key);
            request.onerror = event => {
                this.syncLog.error(event, `${objectStoreName}: Error deleting record from object store.`);
                reject(event);
            };
            request.onsuccess = event => {
                const timeEnd = performance.now();
                this.syncLog.trace(`${objectStoreName}: ${key} deleted from object store in ${Math.round(timeEnd - timeStart)} ms.`);
                resolve();
            };
        });
    }
    count(objectStoreName) {
        return new Promise((resolve, reject) => {
            this.syncLog.trace(`${objectStoreName}: Counting records in object store.`);
            const timeStart = performance.now();
            const transaction = this.transaction(objectStoreName, resolve, reject);
            const objectStore = transaction.objectStore(objectStoreName);
            const request = objectStore.count();
            request.onerror = event => {
                this.syncLog.error(event, `${objectStoreName}: Error counting records in object store.`);
                reject(event);
            };
            request.onsuccess = function () {
                const timeEnd = performance.now();
                this.syncLog.trace(`${objectStoreName}: counted ${request.result} records in ${Math.round(timeEnd - timeStart)} ms.`);
                resolve(request.result);
            };
        });
    }
    clear(model) {
        return new Promise((resolve, reject) => {
            const objectStoreName = model.objectStoreName;
            this.syncLog.trace(`${objectStoreName}: Clearing object store.`);
            const timeStart = performance.now();
            const transaction = this.transaction(objectStoreName, resolve, reject);
            const objectStore = transaction.objectStore(objectStoreName);
            const request = objectStore.clear();
            request.onerror = event => {
                this.syncLog.error(event, `${objectStoreName}: Error clearing object store.`);
                reject(event);
            };
            request.onsuccess = event => {
                const timeEnd = performance.now();
                this.syncLog.trace(`${objectStoreName}: object store cleared in ${Math.round(timeEnd - timeStart)} ms.`);
                resolve();
            };
        });
    }
    getObjectStoresNames() {
        return this.db ? Array.from(this.db.objectStoreNames) : [];
    }
    createObjectStore(objectStoreName, key) {
        return from(new Promise((resolve, reject) => {
            if (!includes(this.getObjectStoresNames(), objectStoreName)) {
                this.syncLog.trace(`DB Upgrade: Creating object store ${objectStoreName}.`);
                const objectStore = this.db.createObjectStore(objectStoreName, key);
                objectStore.createIndex(key.keyPath, key.keyPath, { unique: true });
                resolve();
            }
            else {
                this.syncLog.trace(`DB Upgrade: Object store ${objectStoreName} already exists; skipping upgrade.`);
                resolve();
            }
        }));
    }
    transaction(objectStoreName, resolve, reject) {
        let transaction;
        try {
            transaction = this.db.transaction([objectStoreName], 'readwrite');
        }
        catch (err) {
            this.syncLog.error(err, `${objectStoreName}: Error executing transaction on object store.`);
            reject(err);
        }
        transaction.oncomplete = event => {
            // resolve()
        };
        transaction.onerror = event => {
            this.syncLog.error(event, `${objectStoreName}: Error executing transaction on object store: ${event.target.error}.`);
            reject(event);
        };
        return transaction;
    }
    /**
     * Exports contents of 'fitforce-planner' database in json format
     * @example { "fitforce-db": { "version": 9, "timestamp": "2019-01-14T21_42_27Z", "exercises": [...], "equipment: [...],", etc. } }
     */
    exportToJSON() {
        const promises = [];
        forEach(this.getObjectStoresNames(), (objectStoreName) => {
            const promise = new Promise((resolve, reject) => {
                const transaction = this.transaction(objectStoreName, resolve, reject);
                const objectStore = transaction.objectStore(objectStoreName);
                if (!objectStore) {
                    reject(new Error('Object store does not exist: ' + objectStoreName));
                    return;
                }
                const request = objectStore.getAll();
                request.onsuccess = event => {
                    resolve({
                        name: objectStoreName,
                        values: event.target.result,
                    });
                };
                request.onerror = event => {
                    reject();
                };
            });
            promises.push(promise);
        });
        return Promise.all(promises).then(results => {
            const data = {};
            data[this.options.dbName] = {
                version: this.options.version,
                timestamp: moment$1()
                    .utc()
                    .format(),
                user: JSON.parse(localStorage.getItem('midas-user')),
                'db-last-synced': {},
            };
            forEach(results, result => {
                data[this.options.dbName][result.name] = result.values;
                data[this.options.dbName]['db-last-synced'][result.name] = localStorage.getItem(`db-${result.name}-last-synced`);
            });
            return data;
        });
    }
    /**
     * Clears the browser's fitforce-planner object stores that intersect with the import file, and inserts
     * the values of the properties into indexedbb
     * @param json an object containing data to import to indexeddb
     */
    importFromJSON(json) {
        const fitforceDb = json[this.options.dbName];
        if (!fitforceDb) {
            throw new Error('Invalid database in Import File');
        }
        if (!fitforceDb.version || fitforceDb.version !== this.options.version) {
            throw new Error('Invalid version');
        }
        const promises = [];
        forEach(intersection(Object.keys(this.options.objectStores), Object.keys(fitforceDb)), objectStoreName => {
            const clearPromise = new Promise((resolve, reject) => {
                return this.executeRequest(objectStoreName, objectStore => objectStore.clear(), resolve, reject);
            });
            const promise = clearPromise
                .then(() => {
                const inserts = [];
                fitforceDb[objectStoreName].forEach(value => {
                    const insert = new Promise((resolve, reject) => {
                        return this.executeRequest(objectStoreName, objectStore => objectStore.put(value), resolve, reject);
                    });
                    inserts.push(insert);
                });
                return Promise.all(inserts);
            })
                .catch(err => {
                throw err;
            });
            promises.push(promise);
        });
        return Promise.all(promises);
    }
    /**
     * Executes a transaction on an object store and resolves or rejects the provided promise
     * parameters on success or error
     * @param objectStoreName object store name
     * @param requestDelegate request to execute delegated by caller
     * @param resolve resolve param of promise
     * @param reject reject param of promise
     */
    executeRequest(objectStoreName, requestDelegate, resolve, reject) {
        const transaction = this.transaction(objectStoreName, resolve, reject);
        const objectStore = transaction.objectStore(objectStoreName);
        if (!objectStore) {
            reject(new Error('Object store does not exist: ' + objectStoreName));
            return;
        }
        const request = requestDelegate(objectStore);
        request.onsuccess = event => {
            resolve();
        };
        request.onerror = event => {
            reject();
        };
    }
    seedDatabase() {
        if (this.options.seed) {
            const stores = keys(this.options.objectStores);
            const promises = filter(map(stores, name => {
                const records = this.options.seed[name];
                if (records && records.length > 0) {
                    return this.insert({ objectStoreName: name }, records, true);
                }
            }));
            return Promise.all(promises);
        }
        else {
            return new Promise(resolve => resolve());
        }
    }
    onDatabaseError(event) {
        const err = get(event, 'target.error', event);
        this.syncLog.error(err, `Database error.`);
        console.log('Database error:', err);
    }
}

function connected() {
    throw new Error('Not Implemented');
}
function upgraded(service) {
    service.syncLog.trace('DB Upgrade: Updating object stores.');
    return forkJoin(map(keys(service.options.objectStores), key => service.createObjectStore(key, service.options.objectStores[key])));
}

class GenericRestService {
    constructor(http) {
        this.http = http;
    }
    get(path, params) {
        return this.http.get(this.buildRoute(path), { params }).pipe(catchError(e => this.handleError(e)));
    }
    post(path, data) {
        return this.http.post(this.buildRoute(path), data).pipe(catchError(e => this.handleError(e)));
    }
    delete(path) {
        return this.http.delete(this.buildRoute(path)).pipe(catchError(e => this.handleError(e)));
    }
    patch(path, patch) {
        return this.http.patch(this.buildRoute(path), patch).pipe(catchError(e => this.handleError(e)));
    }
    handleError(e) {
        return throwError(e || 'Server error');
    }
    buildRoute(path) {
        return this.applySubstitutions(this.baseUrl) + 'api/' + path;
    }
    applySubstitutions(url) {
        const myUrl = url.replace(/{{HOSTNAME}}/gi, location.hostname);
        return myUrl;
    }
}
GenericRestService.ɵfac = function GenericRestService_Factory(t) { return new (t || GenericRestService)(ɵɵinject(HttpClient)); };
GenericRestService.ɵprov = ɵɵdefineInjectable({ token: GenericRestService, factory: GenericRestService.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵsetClassMetadata(GenericRestService, [{
        type: Injectable
    }], function () { return [{ type: HttpClient }]; }, null); })();

class OfflineFirstDataService {
    constructor(offline, syncLog, observerService) {
        this.offline = offline;
        this.syncLog = syncLog;
        this.observerService = observerService;
        this.syncProgressSubject = new Subject();
    }
    get syncProgress() {
        return this.syncsDone / (this.syncsTotal || 1);
    }
    updateTotalProgress(interval = 1, skipUpdate = false) {
        this.syncsDone += interval;
        this.syncProgressSubject.next(this.syncProgress);
    }
    /**
     * Initialize the offline-first service.
     *
     * We attempt to restore data from offline cache,
     * if there is some, we use that and are done.
     *
     * If there is no data available, we will attempt
     * to sync from Internet if we can.
     */
    init() {
        return this.restoreData().pipe(concatMap(() => this.hasData()), concatMap(hasData => {
            if (!hasData && this.offline.isOnline()) {
                return this.push().pipe(concatMap(() => this.pull()), concatMap(() => this.restoreData()));
            }
            else {
                return of(null);
            }
        }));
    }
    prepareSync() {
        return of(null);
    }
    /**
     * Force two-way sync with the server
     */
    sync() {
        this.syncProgressSubject.next(0);
        this.observerService.notify('Sync started');
        return this.prepareSync().pipe(
        // first restore offline data into service
        concatMap(() => this.restoreData()), tap(() => {
            this.updateTotalProgress(1);
        }), 
        // then push any updates to the server
        concatMap(() => this.push()), 
        // then pull/fetch new data
        concatMap(() => this.pull()), 
        // pull updated data from database
        concatMap(() => this.restoreData()), tap(() => {
            this.observerService.notify('Sync finished');
            this.updateTotalProgress(1);
        }));
    }
    /**
     * Restore data from offline database into service
     */
    restoreData() {
        return of(null);
    }
    /**
     * Determine if we have all the data we need from offline data store to operate
     */
    hasData() {
        // TODO: determine if data is available for offline use,
        // this defaults to always re-fetching data on each load when there is network available
        return of(false);
    }
    /**
     * Push updated data to server
     */
    push() {
        return of(null);
    }
    /**
     * Override in child class to pull/fetch all necessary data
     */
    pull() {
        return of(null);
    }
    /**
     * Gets many records of type T from the offline database or the server if the resource versions are not equal
     * @param options.request REST request to perform
     * @param options.transform delegate to transform results of request
     * @param options.model DatabaseRecord type to get
     * @param options.clear true clears object store before adding response
     * @param options.returnOnlyUpdated - not implemented
     */
    getMany(options) {
        const modelName = options.model.recordName;
        this.syncLog.debug(`${modelName}: Getting all records from the server…`);
        const timeStart = performance.now();
        // get all records from the server if resource versions aren't equal
        return options.request.pipe(concatMap(records => {
            if (records) {
                const timeEnd = performance.now();
                this.syncLog.debug(`${modelName}: Got ${records.length} record(s) from the server in ${Math.round(timeEnd - timeStart)} ms.`);
                let ob = of(records);
                // transform records if option was provided
                if (options.transform) {
                    records = records.map(options.transform);
                    if (records.length > 0) {
                        if (isObservable(records[0])) {
                            ob = forkJoin(records);
                        }
                        else {
                            ob = of(records);
                        }
                    }
                    else {
                        ob = of([]);
                    }
                }
                return ob.pipe(concatMap((transformedRecords) => {
                    // clear the object store before inserting values from the server if option was provided
                    if (options.clear) {
                        return this.offline.clear(options.model).pipe(concatMap(() => this.offline.save(options.model, transformedRecords)), map$1(() => transformedRecords));
                    }
                    else {
                        return this.offline.save(options.model, transformedRecords).pipe(map$1(() => transformedRecords));
                    }
                }));
            }
            else {
                // if no records were returned, fall back to the offline database
                this.syncLog.debug(`${modelName}: Empty response from the server; returning cached data.`);
                return this.offline.getAll(options.model);
            }
        }));
    }
    /**
     *
     * @param options.model database model to get
     * @param options.uuids uuids to get from the server
     * @param options.request request to get document
     * @param options.diffRequest requst to get document diff
     * @param options.clear optional option to clear object store before saving request response
     */
    getManyDocuments(options) {
        return this.offline.getAll(options.model).pipe(concatMap(records => {
            let request;
            if (records && intersection(records.map(record => record.uuid), options.uuids).length > 0) {
                this.syncLog.debug(`${options.model.name}: Getting updates from the server…`);
                request = forkJoin(records.filter(record => options.uuids.some(uuid => uuid === record.uuid)).map(record => options.diffRequest(record)));
            }
            else {
                this.syncLog.debug(`${options.model.name}: Getting records from the server…`);
                request = forkJoin(options.uuids.map(uuid => options.request(uuid)));
            }
            const timeStart = performance.now();
            return request.pipe(concatMap(documents => {
                const timeEnd = performance.now();
                this.syncLog.debug(`${options.model.name}: Got ${documents.length} record(s) from the server in ${Math.round(timeEnd - timeStart)} ms.`);
                let clearTask = of(null);
                if (options.clear) {
                    clearTask = this.offline.clear(options.model).pipe(concatMap(() => this.offline.save(options.model, documents)), map$1(() => documents));
                }
                return clearTask.pipe(concatMap(() => this.offline.save(options.model, documents).pipe(map$1(() => documents))));
            }));
        }));
    }
    /**
     * Syncs any modified records in an object store with the server
     * @param model Database Record Type to sync
     * @param options.pre optional operation to perform pre-sync
     * @param options.created request to perform if created
     * @param options.updated request to perform if updated
     * @param options.deleted request to perform if deleted
     */
    syncDirty(model, options) {
        const records = isObservable(model) ? model : this.offline.getAll(model);
        return records.pipe(concatMap(allRecords => {
            const updatedRecords = allRecords.filter(record => record && record.isDirty);
            const obs = [];
            if (updatedRecords.length > 0) {
                this.syncLog.debug(`${options.model.recordName}: Syncing ${updatedRecords.length} record(s) to the server…`);
            }
            updatedRecords.forEach(record => {
                if (options.pre) {
                    options.pre(record);
                }
                let operation;
                if (record.createdAt) {
                    operation = options.created(record);
                }
                else if (record.updatedAt && record.updatedAt.isSame(record.lastUpdate)) {
                    operation = options.updated(record);
                }
                else if (record.deletedAt && record.deletedAt.isSame(record.lastUpdate)) {
                    operation = options.deleted(record).pipe(catchError(err => {
                        if (err instanceof HttpErrorResponse && err.status === 404) {
                            this.syncLog.warn(`${options.model.recordName}: ${record.key} has already been deleted.`);
                            // if we got a 404 error when trying to delete something,
                            // it has already been deleted. ignore the error and continue
                            return of(record);
                        }
                        else {
                            return throwError(err);
                        }
                    }));
                }
                obs.push(operation);
            });
            if (obs.length === 0) {
                return of([]);
            }
            else {
                const timeStart = performance.now();
                return forkJoin(obs).pipe(concatMap(results => this.cleanUpRecords(updatedRecords, options.model)), tap(() => {
                    const timeEnd = performance.now();
                    this.syncLog.debug(`${options.model.recordName}: Synced ${updatedRecords.length} record(s) with server in ${Math.round(timeEnd - timeStart)} ms.`);
                }));
            }
        }));
    }
    cleanUpRecords(records, model) {
        const obs = [];
        const puts = records.filter(rec => rec.createdAt || rec.updatedAt);
        const deletes = records.filter(rec => rec.deletedAt);
        puts.forEach(rec => rec.markClean());
        deletes.forEach(rec => rec.markClean());
        if (puts.length > 0) {
            this.syncLog.trace(`${model.recordName}: Unmarking ${puts.length} record(s) for syncing in the object store...`);
            obs.push(this.offline.save(model, puts));
        }
        if (deletes.length > 0) {
            this.syncLog.trace(`${model.recordName}: Deleting ${deletes.length} deleted record(s) from the object store...`);
            obs.push(deletes.map((record) => this.offline.delete(model, record)));
        }
        let cleanUp;
        if (obs.length > 0) {
            cleanUp = forkJoin(obs).pipe(map$1(() => null));
        }
        else {
            cleanUp = of();
        }
        return cleanUp.pipe(map$1(() => concat(puts, deletes)));
    }
    deleteRecord(model, record) {
        return this.offline.getRecord(model, record.key).pipe(concatMap(restoredRecord => {
            if (restoredRecord.createdAt) {
                return this.offline.delete(model, restoredRecord);
            }
            else {
                restoredRecord.markDeleted();
                return this.offline.save(model, restoredRecord);
            }
        }));
    }
}

var OfflineReason;
(function (OfflineReason) {
    OfflineReason["ONLINE"] = "Currently online.";
    OfflineReason["FORCED"] = "User manually set to offline mode.";
    OfflineReason["BAD_NETWORK"] = "Bad network connectivity to Fitforce server.";
    OfflineReason["NO_NETWORK"] = "The device is unable to access the Internet.";
})(OfflineReason || (OfflineReason = {}));
class OfflineService {
    constructor() {
        this.statusSubject = new BehaviorSubject(navigator.onLine);
        this.offlineForced = false;
        this.status = this.statusSubject.asObservable();
        this.online = navigator.onLine;
        this.setOfflineStatusReason();
        window.addEventListener('online', this.updateStatus.bind(this));
        window.addEventListener('offline', this.updateStatus.bind(this));
    }
    isOnline() {
        return this.online && !this.offlineForced;
    }
    isOffline() {
        return !this.online || this.offlineForced;
    }
    isNavigatorOnline() {
        return navigator.onLine;
    }
    isOfflineForced() {
        return this.offlineForced;
    }
    forceOffline(setOffline, reason) {
        // Do not allow user to get online if navigator is offline
        if (!setOffline && !navigator.onLine) {
            return;
        }
        this.offlineForced = setOffline;
        this.online = !setOffline;
        if (this.online) {
            reason = OfflineReason.ONLINE;
        }
        this.offlineReason = reason;
        this.statusSubject.next(this.online);
    }
    /**
     * Implement in child class
     */
    getRecord(model, where) {
        return of(null);
    }
    /**
     * Implement in child class
     */
    getAll(model) {
        return of(null);
    }
    /**
     * Implement in child class
     */
    clear(model) {
        return of(null);
    }
    /**
     * Implement in child class
     */
    query(model, where) {
        return of(null);
    }
    /**
     * Implement in child class
     */
    queryUndeleted(model, where) {
        return of(null);
    }
    /**
     * Implement in child class
     */
    save(model, records) {
        return of(null);
    }
    /**
     * Implement in child class
     */
    delete(model, record) {
        return of(null);
    }
    /**
     * Implement in child class
     */
    deleteByKey(model, key) {
        return of(null);
    }
    /**
     * Implement in child class
     */
    count(model) {
        return of(null);
    }
    updateStatus() {
        if (!this.offlineForced && !navigator.onLine) {
            this.online = navigator.onLine;
        }
        this.setOfflineStatusReason();
        this.statusSubject.next(this.online);
    }
    setOfflineStatusReason() {
        if (this.online) {
            this.offlineReason = OfflineReason.ONLINE;
        }
        else if (this.offlineForced) {
            this.offlineReason = OfflineReason.FORCED;
        }
        else {
            this.offlineReason = OfflineReason.NO_NETWORK;
        }
    }
}

class AppUpdateDialogComponent {
    constructor() { }
    ngOnInit() { }
}
AppUpdateDialogComponent.ɵfac = function AppUpdateDialogComponent_Factory(t) { return new (t || AppUpdateDialogComponent)(); };
AppUpdateDialogComponent.ɵcmp = ɵɵdefineComponent({ type: AppUpdateDialogComponent, selectors: [["ff-app-update-dialog"]], decls: 9, vars: 1, consts: [["mat-dialog-title", ""], ["fxLayout", "row", "fxLayoutAlign", "end"], ["mat-button", "", "mat-dialog-close", ""], ["mat-button", "", "mat-raised-button", "", "color", "primary", 3, "mat-dialog-close"]], template: function AppUpdateDialogComponent_Template(rf, ctx) { if (rf & 1) {
        ɵɵelementStart(0, "h2", 0);
        ɵɵtext(1, "FitForce");
        ɵɵelementEnd();
        ɵɵelementStart(2, "mat-dialog-content");
        ɵɵtext(3, "A new version of the app has been installed.");
        ɵɵelementEnd();
        ɵɵelementStart(4, "mat-dialog-actions", 1);
        ɵɵelementStart(5, "button", 2);
        ɵɵtext(6, "Later");
        ɵɵelementEnd();
        ɵɵelementStart(7, "button", 3);
        ɵɵtext(8, "Update");
        ɵɵelementEnd();
        ɵɵelementEnd();
    } if (rf & 2) {
        ɵɵadvance(7);
        ɵɵproperty("mat-dialog-close", true);
    } }, directives: [MatDialogTitle, MatDialogContent, MatDialogActions, DefaultLayoutDirective, DefaultLayoutAlignDirective, MatButton, MatDialogClose], styles: [""] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵsetClassMetadata(AppUpdateDialogComponent, [{
        type: Component,
        args: [{
                // tslint:disable-next-line: component-selector
                selector: 'ff-app-update-dialog',
                templateUrl: './app-update-dialog.component.html',
                styleUrls: ['./app-update-dialog.component.scss'],
            }]
    }], function () { return []; }, null); })();

class ServiceWorkerService {
    constructor(dialog, appRef, sw) {
        this.dialog = dialog;
        this.appRef = appRef;
        this.sw = sw;
        this.initialized = false;
    }
    /**
     * Initializes service and automatically polls server for client updates
     * @param checkInterval interval to check for updates
     */
    init(checkInterval) {
        if (!this.initialized && this.sw.isEnabled) {
            checkInterval = checkInterval || 60 * 1000;
            concat$1(
            // Allow the app to stabilize before starting polling for updates with `interval()`.
            this.appRef.isStable.pipe(first$1(isStable => isStable === true)), interval(checkInterval)).subscribe(() => {
                if (navigator.onLine) {
                    this.sw.checkForUpdate();
                }
            });
            this.sw.available.subscribe((event) => {
                this.dialog
                    .open(AppUpdateDialogComponent)
                    .beforeClosed()
                    .subscribe(result => {
                    if (result === true) {
                        this.sw.activateUpdate().then(() => {
                            document.location.reload();
                        });
                    }
                });
            });
            this.initialized = true;
        }
    }
    checkForUpdate() {
        if (this.sw.isEnabled && navigator.onLine) {
            this.appRef.isStable.pipe(first$1(isStable => isStable === true)).subscribe(() => {
                this.sw.checkForUpdate();
            });
        }
    }
}
ServiceWorkerService.ɵfac = function ServiceWorkerService_Factory(t) { return new (t || ServiceWorkerService)(ɵɵinject(MatDialog), ɵɵinject(ApplicationRef), ɵɵinject(SwUpdate)); };
ServiceWorkerService.ɵprov = ɵɵdefineInjectable({ token: ServiceWorkerService, factory: ServiceWorkerService.ɵfac, providedIn: 'root' });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵsetClassMetadata(ServiceWorkerService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: MatDialog }, { type: ApplicationRef }, { type: SwUpdate }]; }, null); })();

class ObserverService {
    constructor() {
        this.subject = new Subject();
    }
    observe(topic) {
        return this.subject.asObservable().pipe(filter$1(obs => {
            return topic === obs.topic;
        }));
    }
    notify(topic, data) {
        this.subject.next({ topic, data });
    }
}
ObserverService.ɵfac = function ObserverService_Factory(t) { return new (t || ObserverService)(); };
ObserverService.ɵprov = ɵɵdefineInjectable({ token: ObserverService, factory: ObserverService.ɵfac, providedIn: 'root' });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵsetClassMetadata(ObserverService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return []; }, null); })();

class OfflineFirstModule {
}
OfflineFirstModule.ɵmod = ɵɵdefineNgModule({ type: OfflineFirstModule });
OfflineFirstModule.ɵinj = ɵɵdefineInjector({ factory: function OfflineFirstModule_Factory(t) { return new (t || OfflineFirstModule)(); }, imports: [[BrowserModule, BrowserAnimationsModule, MatDialogModule, MatButtonModule, FlexLayoutModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && ɵɵsetNgModuleScope(OfflineFirstModule, { declarations: [AppUpdateDialogComponent], imports: [BrowserModule, BrowserAnimationsModule, MatDialogModule, MatButtonModule, FlexLayoutModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵsetClassMetadata(OfflineFirstModule, [{
        type: NgModule,
        args: [{
                imports: [BrowserModule, BrowserAnimationsModule, MatDialogModule, MatButtonModule, FlexLayoutModule],
                declarations: [AppUpdateDialogComponent],
            }]
    }], null, null); })();

/*
 * Public API Surface of fitforce-offline-first
 */

/**
 * Generated bundle index. Do not edit.
 */

export { DatabaseRecord, DatabaseService, DatabaseType, DocumentRecord, GenericRestService, ObserverService, OfflineFirstDataService, OfflineFirstModule, OfflineReason, OfflineService, ServiceWorkerService, connected, upgraded };
//# sourceMappingURL=fitforce-offline-first.js.map
